import { IconBotOutline } from '@icons/IconBotOutline'
import { IconPersonOutline } from '@icons/IconPersonOutline'
import { ChatMessage } from './types'
import { motion } from 'motion/react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { MermaidRenderer } from '../component-library/MdTextComponent/MermaidRenderer'
import { IconSurveyScale5Outline } from '@icons/IconSurveyScale5Outline'

// Individual message bubble component
export const MessageBubble: React.FC<{ message: ChatMessage }> = ({ message }) => {
  const isUser = message.role === 'user'

  return (
    <motion.div
      className={`mb-10 flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
      initial={{
        opacity: 0,
        x: isUser ? 50 : -50,
        scale: 0.95,
      }}
      animate={{
        opacity: 1,
        x: 0,
        scale: 1,
      }}
      transition={{
        type: 'spring',
        stiffness: 500,
        damping: 30,
        duration: 0.3,
      }}
    >
      {/* Avatar */}
      <div
        className={`flex h-8 w-8 flex-shrink-0 translate-y-4 items-center justify-center rounded-full ${
          isUser ? '!translate-y-0.5 bg-accent text-background' : 'bg-offwhite text-muted-foreground/80'
        }`}
      >
        {isUser ? <IconPersonOutline size={20} /> : <IconSurveyScale5Outline size={20} />}
      </div>

      {/* Message content */}
      <div className={`${isUser ? 'max-w-[80%] text-right' : 'flex-1 text-left'}`}>
        <div className={`${isUser ? 'inline-block rounded-2xl rounded-br-sm bg-accent p-3 text-background' : ''}`}>
          {isUser ? (
            <p className="whitespace-pre-wrap text-sm">{message.content}</p>
          ) : (
            <div className="border-offwhite bg-offwhite prose prose-sm max-w-none rounded-xl border p-5 text-sm">
              <Markdown
                remarkPlugins={[remarkGfm]}
                components={{
                  pre({ children, ...props }) {
                    // Check if this pre contains a code block with mermaid
                    const codeElement = children as React.ReactElement
                    if (codeElement?.props?.className?.includes('language-mermaid')) {
                      return <MermaidRenderer chart={String(codeElement.props.children).replace(/\n$/, '')} />
                    }
                    return <pre {...props}>{children}</pre>
                  },
                  code({ className, children, ...props }) {
                    // For inline code or non-mermaid code blocks
                    return (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    )
                  },
                }}
              >
                {message.content}
              </Markdown>
            </div>
          )}
        </div>
        <p className="mt-1 px-3 text-xs text-gray-500">{new Date(message.timestamp).toLocaleTimeString()}</p>
      </div>
    </motion.div>
  )
}
